import { type SyntheticEvent, useState, type ReactElement, useEffect } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Grid, styled, Button, Slider, Input, Collapse, IconButton } from '@mui/material'
import { H1, Body2White, Body1White } from '@/components/typography'
import css from './styles.module.css'
import { useAppDispatch } from '@/store'
import { Close, PersonAdd, Edit, Delete } from '@mui/icons-material'
import useDesktop from '@/hooks/useDesktop'
import EthHashInfo from '@/components/common/EthHashInfo'
import TextInputComponent from '@/components/common/TextInputComponent'
import useSafeAddress from '@/hooks/useSafeAddress'
import useWallet from '@/hooks/wallets/useWallet'
import { validateAddress } from '@/utils/validation'
import { sameAddress } from '@/utils/addresses'
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined'
import { trackEvent, INHERIT_EVENTS } from '@/services/analytics'

import {
  type Inheritor,
  setInheritors,
  setActiveStep,
  setStep1Done,
  type InheritanceStep,
} from '@/store/setUpInheritorsV2Slice'

const PrettoSlider = styled(Slider)({
  color: '#5D5DFF',
  height: 4,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 12,
    borderRadius: '2px',

    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
})

const AddInheritors = ({
  chainIdAddress,
  inheritanceStep,
}: {
  chainIdAddress: string
  inheritanceStep: InheritanceStep
}): ReactElement => {
  const router = useRouter()
  const isDesktop = useDesktop()
  const safeAddress = useSafeAddress()
  const wallet = useWallet()

  const isCoinbase = wallet?.label && wallet.label.toLowerCase().indexOf('coinbase') !== -1 && !isDesktop ? true : false

  const inheritors = inheritanceStep.step1.inheritors

  const dispatch = useAppDispatch()
  const [maxPercentage, setMaxPercentage] = useState<number>(100)
  const [percentage, setPercentage] = useState<number>(0)
  const [warning, setWarning] = useState<boolean>(false)

  const [address, setAddress] = useState<string>('')
  const [addressError, setAddressError] = useState<string | undefined>(undefined)

  const [name, setName] = useState<string>('')
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  const [email, setEmail] = useState<string>('')
  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  useEffect(() => {
    const edit = inheritors.find((inheritor) => inheritor.isEditing)
    if (edit) {
      setAddress(edit.address)
      setName(edit.name)
      setEmail(edit.email)
      setPercentage(edit.percentage)
    }
  }, [])

  useEffect(() => {
    if (inheritors.length === 0) {
      handleAddInheritor()
    }
  }, [inheritors])

  // validate address here
  useEffect(() => {
    if (address === '') {
      setAddressError('Address is required')
      return
    }
    const error = validateAddress(address)
    if (error) {
      setAddressError(error)
      return
    }

    const notEditInheritors = inheritors.filter((inheritor) => !inheritor.isEditing)
    const addressExists = notEditInheritors.some((inheritor) => sameAddress(inheritor.address, address))

    if (addressExists) {
      setAddressError('The inheritor already exists')
      return
    }

    if ((wallet && wallet.address === address) || safeAddress === address) {
      setAddressError(`Can't add yourself as an inheritor`)
      return
    }

    // not smart contract address
    setAddressError(undefined)
  }, [address, inheritors, wallet, safeAddress])

  // validate name here
  useEffect(() => {
    if (name !== '' && !validateName(name)) {
      setNameError('Invalid name')
    } else {
      setNameError(undefined)
    }
  }, [name, inheritors])

  // validate email here
  useEffect(() => {
    if (email !== '' && !validateEmail(email)) {
      setEmailError('Invalid email')
    } else {
      setEmailError(undefined)
    }
  }, [email, inheritors])

  const clearInputs = () => {
    setAddress('')
    setName('')
    setEmail('')
    setPercentage(0)
    // clear errors
    setAddressError(undefined)
    setNameError(undefined)
    setEmailError(undefined)
    setWarning(false)
  }

  const handleNext = () => {
    const newInheritors = inheritors.map((inheritor) => {
      return { ...inheritor, isEditing: false, isDone: true }
    })

    newInheritors.forEach((inheritor) => {
      trackEvent({
        ...INHERIT_EVENTS.ADDED_INHERITOR,
        ...{ label: `${inheritor.address}_${inheritor.percentage}_${inheritor.name}_${inheritor.email}` },
      })
    })

    trackEvent({ ...INHERIT_EVENTS.SET_UP_STEP_DONE, ...{ label: 'Step 1' } })

    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
    dispatch(setStep1Done({ chainIdAddress, isDone: true }))
    dispatch(setActiveStep({ chainIdAddress, activeStep: 1 }))
  }

  const handleDeleteInheritor = (index: number) => {
    const newInheritors = inheritors.filter((inheritor, i) => i !== index)
    // const newInheritors = inheritors.filter((inheritor) => !inheritor.isEditing)
    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
  }

  const handleExpandLeass = (index: number) => {
    const newInheritors = inheritors.map((inheritor, i) => {
      return { ...inheritor, isEditing: false }
    })
    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
  }

  const handleAddInheritor = () => {
    clearInputs()

    const newInheritors = inheritors.map((inheritor) => {
      return { ...inheritor, isEditing: false }
    })

    const newInheritor = {
      name: '',
      address: '',
      email: '',
      percentage: 0,
      isEditing: true,
      isDone: false,
    }
    newInheritors.push(newInheritor)
    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
  }

  const handleInputChange = (id: string, value: string) => {
    // const { id, value } = event.target
    let editedInheritor = inheritors.find((inheritor) => inheritor.isEditing)
    if (!editedInheritor) {
      return
    }

    if (id === 'inheritorAddr') {
      editedInheritor = { ...editedInheritor, address: value }
      setAddress(value)
    }
    if (id === 'name') {
      editedInheritor = { ...editedInheritor, name: value }
      setName(value)
    }
    if (id === 'email') {
      editedInheritor = { ...editedInheritor, email: value }
      setEmail(value)
    }
    const newInheritors = inheritors.map((inheritor) => {
      if (inheritor.isEditing) {
        return editedInheritor as Inheritor
      }
      return inheritor
    })
    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
  }

  const handleEdit = (index: number) => {
    clearInputs()

    const newInheritors = inheritors.map((inheritor, i) => {
      if (i === index) {
        setPercentage(inheritor.percentage)
        setAddress(inheritor.address)
        setName(inheritor.name)
        setEmail(inheritor.email)
        return { ...inheritor, isEditing: true }
      }
      return { ...inheritor, isEditing: false }
    })
    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
  }

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const newPercentage = newValue as number
    setPercentage(newPercentage)
  }

  const handleSliderCommitted = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]) => {
    let percentageAllocated = 0
    inheritors.forEach((inheritor: Inheritor) => {
      if (!inheritor.isEditing) {
        percentageAllocated += inheritor.percentage
      }
    })
    const newMaxPercentage = 100 - percentageAllocated
    setMaxPercentage(newMaxPercentage)

    const newPercentage = newValue as number
    if (newPercentage > newMaxPercentage) {
      setWarning(true)
    } else {
      setWarning(false)
    }
    const _percentage = newPercentage < 1 ? 1 : newPercentage > newMaxPercentage ? newMaxPercentage : newPercentage
    setPercentage(_percentage)

    let editedInheritor = {
      name,
      address,
      email,
      percentage: _percentage,
      isEditing: true,
      isDone: false,
    }
    dispatchEditInheritor(editedInheritor)
  }

  const handleInputPercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value)
    setPercentage(newValue)
  }

  const dispatchEditInheritor = (editedInheritor: Inheritor) => {
    const newInheritors = inheritors.map((inheritor) => {
      if (inheritor.isEditing) {
        return editedInheritor
      }
      return inheritor
    })
    dispatch(setInheritors({ chainIdAddress, inheritors: newInheritors }))
  }

  const handleBlur = () => {
    let percentageAllocated = 0
    inheritors.forEach((inheritor) => {
      if (!inheritor.isEditing) {
        percentageAllocated += inheritor.percentage
      }
    })
    const newMaxPercentage = 100 - percentageAllocated
    setMaxPercentage(newMaxPercentage)

    if (percentage > newMaxPercentage) {
      setWarning(true)
    } else {
      setWarning(false)
    }

    const _percentage = percentage < 1 ? 1 : percentage > newMaxPercentage ? newMaxPercentage : percentage
    setPercentage(_percentage)
    let editedInheritor = {
      name,
      address,
      email,
      percentage: _percentage,
      isEditing: true,
      isDone: false,
    }
    dispatchEditInheritor(editedInheritor)
  }

  const validateInheritor = (inheritor: Inheritor): boolean => {
    const { address, name, email, percentage } = inheritor
    if (address === '') {
      return false
    }
    const error = validateAddress(address)
    if (error) {
      return false
    }
    if ((wallet && wallet.address === address) || safeAddress === address) {
      return false
    }

    const addressFound = inheritors.filter((inheritor) => sameAddress(inheritor.address, address))
    if (addressFound.length > 1) {
      return false
    }

    if (percentage === 0) {
      return false
    }
    if (name !== '' && !validateName(name)) {
      return false
    }
    if (email !== '' && !validateEmail(email)) {
      return false
    }
    return true
  }

  // validate email input
  const validateEmail = (email: string) => {
    const re = /\S+@\S+\.\S+/
    return re.test(email)
  }

  // validate name input not special characters
  const validateName = (name: string) => {
    // const re = /^[a-zA-Z0-9_ ]*$/
    return name.length <= 100
  }

  const renderInheritor = (index: number, inheritor: Inheritor) => {
    return (
      <Grid
        key={`inheritor_${index}`}
        container
        px="16px"
        py="8px"
        mb="8px"
        sx={{
          borderRadius: '4px',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
        }}
      >
        <Grid item container xs={12} sm={10}>
          <Grid item container xs="auto" sm="auto" alignContent="center" justifyContent="flex-start">
            <Body1White
              sx={{
                width: '64px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                borderRadius: '4px',
                paddingX: isDesktop ? '16px' : '8px',
                paddingY: '8px',
                color: inheritor.percentage !== 0 ? 'var(--Emerald-600, #22C55E)' : 'var(--Ebony-Clay-700, #485581)',
                textAlign: 'center',
              }}
            >
              {inheritor.percentage !== 0 ? `${inheritor.percentage}%` : '-%-'}
            </Body1White>
          </Grid>
          <Grid item xs={9} sm="auto" pl={isDesktop ? '16px' : '8px'}>
            <Body1White sx={{ lineHeight: '140%', color: 'var(--Ebony-Clay-300, #D2D7E5)' }}>
              {inheritor.name === '' ? `Inheritor ${index + 1}` : inheritor.name}
            </Body1White>
            <Body2White
              sx={{
                lineHeight: '140%',
                color: !inheritor.email ? 'var(--Ebony-Clay-700, #485581)' : 'var(--Ebony-Clay-400, #AAB3CF)',
              }}
            >
              {inheritor.email || '-- Empty email --'}
            </Body2White>
            {inheritor.address !== '' && (
              <EthHashInfo
                address={inheritor.address}
                hasExplorer={false}
                showCopyButton={false}
                showName={false}
                shortAddress={false}
                showAvatar={false}
                shortenLen={8}
                showPrefix={isDesktop ? true : false}
              />
            )}
            {inheritor.address === '' && (
              <Body2White sx={{ lineHeight: '140%', color: 'var(--Ebony-Clay-700, #485581)' }}>
                -- Inheritor address --
              </Body2White>
            )}
          </Grid>
          {!validateInheritor(inheritor) && (
            <Grid item xs={12}>
              <Body2White sx={{ lineHeight: '140%', color: 'var(--Supporting-Carnation, #FF2239)' }}>
                Please complete the required inputs before proceeding.
              </Body2White>
            </Grid>
          )}
        </Grid>
        <Grid item container spacing={0.2} xs={12} sm alignContent="center" justifyContent="flex-end" my={'4px'}>
          <Grid item xs={6} sm="auto">
            <IconButton
              sx={{
                background: 'var(--Supporting-Dark-BG-700, #272A3E)',
                borderRadius: '4px 0px 0px 4px',
                color: '#D2D7E5',
                width: '100%',
              }}
              onClick={() => handleEdit(index)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Grid>
          <Grid item xs={6} sm="auto">
            <IconButton
              sx={{
                background: 'var(--Supporting-Dark-BG-700, #272A3E)',
                borderRadius: '0px 4px 4px 0px',
                color: '#D2D7E5',
                width: '100%',
              }}
              onClick={() => handleDeleteInheritor(index)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const renderEdit = (index: number) => {
    return (
      <Grid
        key={`edit_${index}`}
        container
        sx={{
          marginBottom: '8px',
          padding: '16px',
          pt: '4px',
          borderRadius: '4px',
          border: '1px solid var(--Ebony-Clay-950, #2F354B)',
        }}
      >
        <Grid item xs={12} textAlign="right" mb="8px">
          <IconButton
            sx={{ p: '4px', color: 'var(--Ebony-Clay-500, #7C8AB4)' }}
            onClick={() => handleExpandLeass(index)}
          >
            <ExpandLessOutlinedIcon />
          </IconButton>
          {inheritors.length > 1 && (
            <IconButton
              sx={{ p: '4px', color: 'var(--Ebony-Clay-500, #7C8AB4)' }}
              onClick={() => handleDeleteInheritor(index)}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Grid>
        <TextInputComponent
          id="inheritorAddr"
          label="Inheritor address"
          value={address}
          onChange={handleInputChange}
          errorMessages={addressError}
          showQRButton={true}
        />
        {/* {renderInput('inheritorAddr', 'Inheritor address', address, handleInputChange, addressError, true)} */}
        <Grid item xs={12} mb="10px">
          <Body2White>Inheritor percentage</Body2White>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            sx={{
              background: 'var(--Supporting-Dark-BG-700, #272A3E)',
              borderRadius: '4px',
            }}
            px={isDesktop ? '16px' : '8px'}
            py="8px"
          >
            <Grid item xs="auto">
              <Input
                // autoFocus={isCoinbase}
                sx={{
                  background: 'var(--Ebony-Clay-900, #343C58)',
                  borderRadius: '4px',
                  py: '6px',
                  px: isDesktop ? '12px' : '4px',
                  width: 'auto',
                  marginRight: isDesktop ? '16px' : '8px',
                }}
                disableUnderline
                value={Number(percentage).toString()}
                size="small"
                onChange={handleInputPercentageChange}
                onBlur={handleBlur}
                inputProps={{
                  style: { textAlign: 'center', padding: 0 },
                  step: 1,
                  min: 0,
                  max: 100,
                  type: 'number',
                  'aria-labelledby': 'input-slider',
                }}
                endAdornment={<Body2White>%</Body2White>}
              />
            </Grid>
            <Grid item container alignItems="center" justifyContent="center" xs>
              {isCoinbase && (
                <Body2White sx={{ color: 'var(--Ebony-Clay-600, #5B6B9C)', fontSize: '14px' }}>
                  Type percentage in the left box
                </Body2White>
              )}
              {!isCoinbase && (
                <PrettoSlider
                  value={typeof percentage === 'number' ? percentage : 1}
                  onChange={handleSliderChange}
                  onChangeCommitted={handleSliderCommitted}
                  aria-labelledby="input-slider"
                />
              )}
            </Grid>
          </Grid>
          {warning && (
            <Body2White sx={{ color: 'var(--Supporting-Koromiko, #FC8313)' }}>
              Only {maxPercentage}% of the inheritance can be allocated.
            </Body2White>
          )}
        </Grid>
        <TextInputComponent
          id="name"
          label="Inheritor name (optional)"
          value={name}
          onChange={handleInputChange}
          errorMessages={nameError}
        />
        <TextInputComponent
          id="email"
          label="Inheritor email (optional)"
          value={email}
          onChange={handleInputChange}
          errorMessages={emailError}
        />
        {/* {renderInput('name', 'Inheritor name (optional)', name, handleInputChange, nameError)} */}
        {/* {renderInput('email', 'Inheritor email (optional)', email, handleInputChange, emailError)} */}
      </Grid>
    )
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Link href={{ pathname: '/home', query: { wallet: router.query.wallet } }} passHref>
              <Button
                variant="text"
                sx={{
                  paddingY: '10px',
                  width: '100%',
                  borderRadius: '4px',
                  background: 'var(--Ebony-Clay-950, #2F354B)',
                  color: 'var(--Ebony-Clay-300, #D2D7E5)',
                  ':hover': {
                    background: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
                onClick={() => trackEvent(INHERIT_EVENTS.CLICK_CANCEL_SET_UP_INHERITANCE)}
              >
                Cancel
              </Button>
            </Link>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              disabled={inheritors.length === 0 || inheritors.some((inheritor) => !validateInheritor(inheritor))}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleNext}
            >
              Next
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid
      container
      sx={
        isDesktop
          ? {
              borderRadius: '4px',
              border: '1px solid var(--Ebony-Clay-950, #2F354B)',
              padding: '16px',
            }
          : {}
      }
    >
      <Grid item container xs={12} mb="16px">
        <Grid item container xs={7} sm={8} flexDirection="column" justifyContent="center">
          <H1>1. Add inheritors</H1>
        </Grid>
        <Grid item xs={5} sm={4} textAlign="right">
          <Button
            sx={{
              border: '1px solid var(--Blue-Ribbon-600, #5D5DFF)',
              color: 'var(--Ebony-Clay-300, #D2D7E5)',
              fontSize: '14px',
              fontWeight: 'bold',
              lineHeight: '160%',
              paddingY: '4px',
              paddingX: '16px',
              height: '32px',
              width: '100%',
              '&:hover': {
                background: 'var(--Blue-Ribbon-600, #5D5DFF)',
                border: '1px solid var(--Blue-Ribbon-600, #5D5DFF)',
              },
            }}
            startIcon={<PersonAdd fontSize="small" />}
            onClick={handleAddInheritor}
            disabled={
              inheritors.length >= 10 || inheritors.reduce((acc, inheritor) => acc + inheritor.percentage, 0) === 100
            }
          >
            Add new
          </Button>
        </Grid>
      </Grid>

      <Grid item xs={12} className={css.addInheritorContent}>
        {inheritors.length > 0
          ? inheritors.map((inheritor, index) => {
              return (
                <div key={`render_${index}`}>
                  <Collapse in={!inheritor.isEditing} key={`inheritor_${index}`}>
                    {renderInheritor(index, inheritor)}
                  </Collapse>
                  <Collapse in={inheritor.isEditing} key={`edit_${index}`}>
                    {renderEdit(index)}
                  </Collapse>
                </div>
              )
            })
          : renderEdit(0)}
      </Grid>
      {renderFooter()}
    </Grid>
  )
}

export default AddInheritors
